'use client';

import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { cn } from '@/lib/ui/utils';
import type { AvatarProps } from '@radix-ui/react-avatar';
import { useEffect, useMemo, useRef, useState } from 'react';

interface UserAvatarProps extends Omit<AvatarProps, 'children'> {
  name: string;
  image: string | null | undefined;
}

/**
 * User avatar component, used in navbar and sidebar
 * Optimized to prevent layout shifts during image loading
 *
 * @param name - The name of the user
 * @param image - The image of the user
 * @param props - The props of the avatar
 * @returns The user avatar component
 */
export function UserAvatar({
  name,
  image,
  className,
  ...props
}: UserAvatarProps) {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const imageSrc = useMemo(() => {
    if (typeof image !== 'string') return '';
    return image.trim();
  }, [image]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!imageSrc) {
      setIsLoaded(false);
      setHasError(false);
      return;
    }

    setHasError(false);
    setIsLoaded(
      Boolean(imageRef.current?.complete && imageRef.current.naturalWidth > 0)
    );
  }, [imageSrc]);

  const showFallback = !imageSrc || hasError || !isLoaded;
  return (
    <div
      className={cn(
        'relative flex size-8 shrink-0 overflow-hidden rounded-pill bg-muted',
        className
      )}
      {...props}
    >
      <span className="sr-only">{name}</span>

      {imageSrc && !hasError ? (
        <img
          ref={imageRef}
          alt={name}
          title={name}
          src={imageSrc}
          className="absolute inset-0 size-full object-cover"
          loading="eager"
          decoding="async"
          onLoad={() => {
            setIsLoaded(true);
            setHasError(false);
          }}
          onError={() => {
            setIsLoaded(false);
            setHasError(true);
          }}
        />
      ) : null}

      <div
        className={cn(
          'absolute inset-0 flex items-center justify-center transition-opacity',
          showFallback ? 'opacity-100' : 'opacity-0'
        )}
        aria-hidden
      >
        {renderAppIcon({ name: 'user-2', className: 'size-4' })}
      </div>
    </div>
  );
}
